import {routing as routingShared} from '@cl/shared';
import {accountPrefix} from '../routing.consts';
import type {AppRouteParams, AppRouteQueryParams, RouteWithParams} from '../routing.types';
import getCurrentRouteKey from './getCurrentRouteKey';
import parsePathParam from './parsePathParam';
import parseQueryParam from './parseQueryParam';

function getCurrentRouteWithParams(): RouteWithParams | null {
  const routeKey = getCurrentRouteKey();

  if (!routeKey) {
    return null;
  }

  let currentPath = window.location.pathname;

  // Remove account prefix from path if present, since route definitions don't include it
  // accountPrefix is '/acc/:accountId' which translates to '/acc/<number>' in actual paths
  const accountPrefixPattern = accountPrefix.replace(':accountId', '\\d+');
  const accountPrefixRegex = new RegExp(`^${accountPrefixPattern}`);

  currentPath = currentPath.replace(accountPrefixRegex, '');

  // Get the route definition to understand parameter types
  const routeDef = routingShared.allRouteDefs[routeKey];

  if (!routeDef) {
    // If no route definition found, return basic structure without type conversion
    return {
      routeKey,
      pathParamValues: {},
      queryParamValues: {},
    };
  }

  // Extract raw path parameters by comparing route pattern with current path
  const routeParts = routeDef.path.split('/').slice(1); // Remove leading slash
  const pathParts = currentPath.split('/').slice(1); // Remove leading slash

  const rawPathParams: Record<string, string> = {};

  for (let i = 0; i < routeParts.length; i++) {
    const routePart = routeParts[i];
    const pathPart = pathParts[i];

    if (routePart.startsWith(':')) {
      // This is a parameter, extract the name and value
      const paramName = routePart.substring(1); // Remove the colon
      rawPathParams[paramName] = pathPart;
    }
  }

  // Parse path parameters according to their definitions
  const pathParamValues: AppRouteParams = {};

  for (const pathParamDef of routeDef.pathParams) {
    const rawValue = rawPathParams[pathParamDef.key];

    if (rawValue !== undefined) {
      try {
        pathParamValues[pathParamDef.key] = parsePathParam(rawValue, pathParamDef);
      } catch (error) {
        console.warn(`Error parsing path parameter ${pathParamDef.key}:`, error);
        // Fall back to string value if parsing fails
        pathParamValues[pathParamDef.key] = rawValue;
      }
    } else if (pathParamDef.defaultValue) {
      pathParamValues[pathParamDef.key] = pathParamDef.defaultValue;
    }
  }

  // Extract raw query parameters from URL
  const urlSearchParams = new URLSearchParams(window.location.search);
  const rawQueryParams: Record<string, string | string[]> = {};

  for (const [key, value] of urlSearchParams.entries()) {
    if (rawQueryParams[key]) {
      // Multiple values for the same key - convert to array
      if (Array.isArray(rawQueryParams[key])) {
        (rawQueryParams[key] as string[]).push(value);
      } else {
        rawQueryParams[key] = [rawQueryParams[key] as string, value];
      }
    } else {
      rawQueryParams[key] = value;
    }
  }

  // Parse query parameters according to their definitions
  const queryParamValues: AppRouteQueryParams = {};

  for (const queryParamDef of routeDef.queryParams) {
    const rawValue = rawQueryParams[queryParamDef.key];

    if (rawValue !== undefined) {
      try {
        queryParamValues[queryParamDef.key] = parseQueryParam(rawValue, queryParamDef);
      } catch (error) {
        console.warn(`Error parsing query parameter ${queryParamDef.key}:`, error);
        // Fall back to string value if parsing fails
        queryParamValues[queryParamDef.key] = Array.isArray(rawValue) ? rawValue[0] : rawValue;
      }
    } else if (queryParamDef.defaultValue !== undefined) {
      queryParamValues[queryParamDef.key] = queryParamDef.defaultValue;
    }
  }

  // Add any undefined query parameters from URL as strings
  const definedParamKeys = new Set(routeDef.queryParams.map((def) => def.key));

  for (const [key, rawValue] of Object.entries(rawQueryParams)) {
    if (!definedParamKeys.has(key)) {
      // Add undefined query parameter as string
      queryParamValues[key] = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    }
  }

  return {
    routeKey,
    pathParamValues,
    queryParamValues,
  };
}

export default getCurrentRouteWithParams;
